import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { localDayKey, type AbsenceDTO } from '@wf/shared'
import { ApiError, api } from '../api/client'
import { newUuid } from '../offline/uuid'
import { AbsenceSeriesCard } from './AbsenceSeriesCard'
import { absenceRangeLabel } from './AbsenceRequestCard'
import { VacationRestChip } from './VacationCard'
import { monthLabel, shiftMonth } from './timeMath'
import { tenthsToDays } from './VacationCard'
import { useAbsenceTypes, useCreateAbsence, useDecideAbsence, useForfeitVacation, useSendVacationNotice, useTeamAbsences, useVacationAssistant } from './useZeiten'

/** Tage eines 'YYYY-MM' als 'YYYY-MM-DD'-Liste (UTC-sicher, lokale Helfer-Lehre). */
function daysOfMonth(ym: string): string[] {
  const start = Date.parse(`${ym}-01T00:00:00Z`)
  const out: string[] = []
  for (let i = 0; i < 31; i++) {
    const d = new Date(start + i * 86400_000).toISOString().slice(0, 10)
    if (!d.startsWith(ym)) break
    out.push(d)
  }
  return out
}

/** Verfall-Hinweis-Assistent (2b, 💎): je MA Rest/Übertrag + Hinweis-Protokoll; Senden + geführter Verfall.
 *  Der Server erzwingt die Rechtslage (Verfall ohne dokumentierten Hinweis → 400). */
function VacationAssistantCard() {
  const assistant = useVacationAssistant()
  const send = useSendVacationNotice()
  const forfeit = useForfeitVacation()
  const [confirmForfeitId, setConfirmForfeitId] = useState<number | null>(null)
  const rows = assistant.data?.rows ?? []
  if (assistant.isSuccess && rows.length === 0) return null // kein gepflegtes Urlaubskonto → Karte weg
  const err = send.error ?? forfeit.error
  return (
    <section className="wf-card" aria-label="Verfall-Hinweis-Assistent">
      <h3 className="wf-card__title">Verfall-Hinweis-Assistent</h3>
      <p className="sub">
        Urlaub verfällt nur mit dokumentiertem Hinweis (EuGH). Sende den Hinweis rechtzeitig — die Zustellung wird
        protokolliert; der Mitarbeiter bestätigt in seiner Urlaubskarte.
      </p>
      {assistant.isError ? (
        <p className="wf-absform__err" role="alert">Assistent konnte nicht geladen werden — bitte neu laden.</p>
      ) : null}
      {rows.map((r) => (
        <div key={r.userId} className="wf-absreq">
          <span>
            <b>{r.name}</b>: Rest {tenthsToDays(r.remainingTenths)} Tage
            {r.carryTenths > 0 ? ` (davon ${tenthsToDays(r.carryTenths)} Übertrag)` : ''}
            {r.lastNotice ? (
              <span className="wf-absreq__note">
                {' '}— Hinweis vom {r.lastNotice.sentAt.slice(0, 10)} {r.lastNotice.acknowledgedAt ? '✓ bestätigt' : '(noch unbestätigt)'}
              </span>
            ) : (
              <span className="wf-absreq__note"> — noch kein Hinweis dokumentiert</span>
            )}
          </span>
          <span className="wf-absreq__actions">
            <button
              type="button"
              className="wf-btn wf-btn--sm"
              disabled={send.isPending}
              onClick={() => {
                forfeit.reset() // Review-Fund: alten Verfall-Fehler nicht stehen lassen
                send.mutate({ user_id: r.userId, client_uuid: newUuid() })
              }}
            >
              {r.lastNotice ? 'Hinweis erneut senden' : 'Hinweis senden'}
            </button>
            {r.carryTenths > 0 ? (
              confirmForfeitId === r.userId ? (
                <button
                  type="button"
                  className="wf-btn wf-btn--ghost wf-btn--sm"
                  disabled={forfeit.isPending}
                  onClick={() => {
                    send.reset()
                    forfeit.mutate({ user_id: r.userId }, { onSettled: () => setConfirmForfeitId(null) })
                  }}
                >
                  Wirklich verfallen lassen?
                </button>
              ) : (
                <button type="button" className="wf-btn wf-btn--ghost wf-btn--sm" onClick={() => setConfirmForfeitId(r.userId)}>
                  Übertrag verfallen lassen…
                </button>
              )
            ) : null}
          </span>
        </div>
      ))}
      {err ? (
        <p className="wf-absform__err" role="alert">
          {err instanceof ApiError ? err.message : 'Aktion fehlgeschlagen.'}
        </p>
      ) : null}
    </section>
  )
}

/**
 * Chef-Tab „Abwesenheiten": offene Anträge entscheiden (Ablehnung mit Pflicht-Grund), Direkteintrag
 * (typisch Krankmeldung — sofort genehmigt) und der Monats-Team-Kalender (Zeilen = Mitarbeiter,
 * Spalten = Tage; Kürzel = genehmigte Abwesenheit). Der Server erzwingt alle Rechte/Guards autoritativ.
 */
export function AbsencesPanel() {
  const [month, setMonth] = useState(() => localDayKey(Date.now()).slice(0, 7))
  const team = useTeamAbsences(month)
  const types = useAbsenceTypes()
  const users = useQuery({ queryKey: ['zeiten', 'absence-users'], queryFn: api.listUsers })
  const decide = useDecideAbsence()
  const create = useCreateAbsence()
  const [rejectId, setRejectId] = useState<number | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  // Direkteintrag-Formular
  const [dUser, setDUser] = useState('')
  const [dType, setDType] = useState('')
  const [dFrom, setDFrom] = useState('')
  const [dTo, setDTo] = useState('')

  const absences = team.data?.absences ?? []
  const open = absences.filter((a) => a.status === 'offen')
  const approved = absences.filter((a) => a.status === 'genehmigt')
  const days = useMemo(() => daysOfMonth(month), [month])
  const teamUsers = (users.data?.users ?? []).filter((u) => u.active === 1)

  const cellFor = (userId: number, day: string): AbsenceDTO | undefined =>
    approved.find((a) => a.userId === userId && a.fromDay <= day && a.toDay >= day)

  const decideErr = decide.error instanceof ApiError ? decide.error.message : decide.isError ? 'Entscheidung fehlgeschlagen.' : null

  return (
    <div className="wf-zstack">
      <section className="wf-card" aria-label="Offene Abwesenheits-Anträge">
        <h3 className="wf-card__title">Offene Anträge</h3>
        {team.isError ? (
          <p className="wf-absform__err" role="alert">
            Anträge konnten nicht geladen werden — bitte neu laden.
          </p>
        ) : null}
        {team.isSuccess && open.length === 0 ? <p className="sub">Keine offenen Anträge.</p> : null}
        {open.map((a) => (
          <div key={a.id} className="wf-absreq">
            <span>
              <b>{a.userName ?? 'Mitarbeiter'}</b>: {a.label} {absenceRangeLabel(a)}
              {a.note ? <span className="wf-absreq__note"> — „{a.note}"</span> : null}
              {a.typeKey === 'urlaub' ? <VacationRestChip userId={a.userId} year={Number(a.fromDay.slice(0, 4))} /> : null}
            </span>
            <span className="wf-absreq__actions">
              <button
                type="button"
                className="wf-btn wf-btn--sm"
                disabled={decide.isPending}
                onClick={() => decide.mutate({ id: a.id, body: { status: 'genehmigt', version: a.version } })}
              >
                Genehmigen
              </button>
              {rejectId === a.id ? (
                <>
                  <input
                    className="wf-input wf-input--sm"
                    placeholder="Grund (Pflicht)"
                    aria-label="Ablehnungs-Grund"
                    value={rejectReason}
                    maxLength={300}
                    onChange={(e) => setRejectReason(e.target.value)}
                  />
                  <button
                    type="button"
                    className="wf-btn wf-btn--ghost wf-btn--sm"
                    disabled={decide.isPending || rejectReason.trim() === ''}
                    onClick={() =>
                      decide.mutate(
                        { id: a.id, body: { status: 'abgelehnt', decision_reason: rejectReason.trim(), version: a.version } },
                        { onSuccess: () => { setRejectId(null); setRejectReason('') } },
                      )
                    }
                  >
                    Ablehnen
                  </button>
                </>
              ) : (
                <button type="button" className="wf-btn wf-btn--ghost wf-btn--sm" onClick={() => { setRejectId(a.id); setRejectReason('') }}>
                  Ablehnen…
                </button>
              )}
            </span>
          </div>
        ))}
        {decideErr ? (
          <p className="wf-absform__err" role="alert">
            {decideErr}
          </p>
        ) : null}
      </section>

      <section className="wf-card" aria-label="Abwesenheit eintragen">
        <h3 className="wf-card__title">Direkt eintragen (z. B. Krankmeldung)</h3>
        <div className="wf-absform wf-absform--row">
          <select className="wf-input" aria-label="Mitarbeiter" value={dUser} onChange={(e) => setDUser(e.target.value)}>
            <option value="">Mitarbeiter…</option>
            {teamUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
          <select className="wf-input" aria-label="Art" value={dType} onChange={(e) => setDType(e.target.value)}>
            <option value="">Art…</option>
            {(types.data?.types ?? [])
              .filter((t) => t.active === 1)
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
          </select>
          <input className="wf-input" type="date" aria-label="Von (Eintrag)" value={dFrom} onChange={(e) => setDFrom(e.target.value)} />
          <input className="wf-input" type="date" aria-label="Bis (Eintrag)" min={dFrom} value={dTo} onChange={(e) => setDTo(e.target.value)} />
          <button
            type="button"
            className="wf-btn"
            disabled={dUser === '' || dType === '' || dFrom === '' || create.isPending}
            onClick={() =>
              create.mutate(
                { type_id: Number(dType), from_day: dFrom, to_day: dTo || dFrom, user_id: Number(dUser), client_uuid: newUuid() },
                { onSuccess: () => { setDUser(''); setDType(''); setDFrom(''); setDTo('') } },
              )
            }
          >
            Eintragen
          </button>
        </div>
        {create.isError ? (
          <p className="wf-absform__err" role="alert">
            {create.error instanceof ApiError ? create.error.message : 'Eintragen fehlgeschlagen.'}
          </p>
        ) : null}
      </section>

      <VacationAssistantCard />

      <AbsenceSeriesCard types={types.data?.types ?? []} />

      <section className="wf-card" aria-label="Team-Kalender">
        <div className="wf-abscal__head">
          <h3 className="wf-card__title">Team-Kalender</h3>
          <div className="wf-abscal__nav">
            <button type="button" className="wf-btn wf-btn--ghost wf-btn--sm" onClick={() => setMonth((m) => shiftMonth(m, -1))} aria-label="Vormonat">
              ‹
            </button>
            <span className="wf-abscal__month">{monthLabel(month)}</span>
            <button type="button" className="wf-btn wf-btn--ghost wf-btn--sm" onClick={() => setMonth((m) => shiftMonth(m, 1))} aria-label="Folgemonat">
              ›
            </button>
          </div>
        </div>
        <div className="wf-abscal__scroll">
          <table className="wf-abscal" aria-label={`Abwesenheiten ${monthLabel(month)}`}>
            <thead>
              <tr>
                <th className="wf-abscal__name" scope="col">
                  Mitarbeiter
                </th>
                {days.map((d) => (
                  <th key={d} scope="col">
                    {Number(d.slice(8, 10))}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {teamUsers.map((u) => (
                <tr key={u.id}>
                  <th className="wf-abscal__name" scope="row">
                    {u.name}
                  </th>
                  {days.map((d) => {
                    const a = cellFor(u.id, d)
                    return (
                      <td key={d} className={a ? `wf-abscal__cell wf-abscal__cell--${a.effect}` : 'wf-abscal__cell'} title={a ? `${a.label} (${a.userName ?? u.name})` : undefined}>
                        {a ? a.label.slice(0, 1) : ''}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}
